/**
 * MypageOrderContainer
 * --
 */
import React from 'react';
import MypageOrderPresenter from './MypageOrderPresenter';
import {
  useUserDataAction,
  useUserDataStack,
  useUserStack,
  useUserAction,
} from 'store';
import { useConfirmParcelServiceStatus } from 'hooks/ParcelServiceHooks';
import MessageAlert, {
  PAYMENT_STORE_ID,
  POINT_STORE_ID,
  getCookie,
  deleteCookie,
} from 'utils';
import {
  useCreateRefundDelivery,
  useCreateRefundRental,
  useCreateRefundSubscription,
} from 'hooks/RefundHooks';
import {
  usePaymentCancelAccount,
  usePaymentCancelCard,
  usePaymentCancelMobile,
} from 'hooks/PaymentHooks.';
import { useMyShopOrderList } from 'hooks/custom';

const MypageOrderContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, searchParams, screenSize } = props;
  const deliveryType = searchParams.get('type') ?? 'delivery';

  /* ===== Mutate ===== */
  const { confirmParcelServiceStatus } = useConfirmParcelServiceStatus();
  const { createRefundDelivery } = useCreateRefundDelivery();
  const { createRefundSubscription } = useCreateRefundSubscription();
  const { cancelCard } = usePaymentCancelCard();
  const { cancelAccount } = usePaymentCancelAccount();
  const { cancelMobile } = usePaymentCancelMobile();
  const { createRefundRental } = useCreateRefundRental();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();
  const userDataAction = useUserDataAction();

  /* ===== Hooks ===== */
  useMyShopOrderList(userInfo.user_id); // 주문내역 데이터 호출 후 스토어 저장 로직 실행

  /* ===== Store ===== */
  const { reviews, orderList } = useUserDataStack();

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // Tap
  const [selectedTab, setSelectedTab] = React.useState(
    deliveryType.toLocaleUpperCase(),
  );
  const [isWebView, setIsWebView] = React.useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 구매확정, 취소/환불
   * --
   * @param {string} type
   * @param {object} data
   * @returns
   */
  const handleOrderStatus = async (type, data) => {
    const submitType = type.toLocaleUpperCase();

    try {
      const confirm = (msg) => {
        return window.confirm(msg);
      };
      switch (submitType) {
        // SECTION: 구매확정
        case 'DELIVERY-CONFIRMED': {
          if (
            !confirm(
              '상품 주문을 확정하시겠습니까?\n주문 확정 후 취소/환불은 불가능합니다.',
            )
          )
            return;

          /**
           * STUB data 형식
           * --
           *  {
           *    shop_order_id: number
           *
           *    parcel_service_id: number
           *    purchase_status: string
           *  }
           */
          const { status: confirmedStatus } =
            await confirmParcelServiceStatus(data);

          if (confirmedStatus !== 200) {
            MessageAlert.warning('주문 확정에 실패하였습니다!');
            return;
          }

          const newOrderList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'CONFIRMED',
                        },
                  ),
                },
          );
          userDataAction.setData('orderList', newOrderList);

          MessageAlert.success('상품 주문이 확정되었습니다.');
          return;
        }
        // SECTION: 주문취소
        case 'STORE_PICKUP-CANCELLED':
        case 'DELIVERY-CANCELLED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    payment_info: object
           *    payment_method: string
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *    tax_free: number
           *  }
           */
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          const {
            shop_order_id,
            payment_method,
            payment_info,
            parcel_service_id,
            refund_reason,
            refund_amount,
            refund_num,
            tax_free,
          } = data;

          let cancelRes = null;

          // 해당 주문건 찾기
          const shopOrder = orderList.find(
            (order) => order.shop_order_id === data.shop_order_id,
          );

          const parcel = shopOrder?.parcel_service?.find(
            (par) => par?.parcel_service_id === parcel_service_id,
          );

          // 간편결제건인지 확인
          const isQuickPay = parcel?.card_id ? true : false;

          // 취소 sequence 번호 계산
          const sequence = shopOrder?.parcel_service.reduce((acc, parcel) => {
            const { refund } = parcel;

            if (refund) {
              ++acc;
            }

            return acc;
          }, 1);

          // 마지막 환불건인지 체크
          // 마지막 배송건이면 배송비 리턴
          const isLast =
            shopOrder.parcel_service.length === sequence
              ? (shopOrder.final_delivery_price ?? 0)
              : 0;

          // NOTE: 결제취소
          if (payment_info) {
            const cancelData = {
              mid: isQuickPay ? POINT_STORE_ID : PAYMENT_STORE_ID,
              cancelType: 'PARTIAL',
              orgTradeKeyType: 'TID',
              orgTradeKey: `${
                payment_info.trno ? payment_info.trno : payment_info.tid
              }`,
              cancelTotalAmount: `${refund_amount + isLast}`,
              cancelTaxFreeAmount: `${tax_free ?? 0}`,
              cancelSeq: `${sequence}`,
            };

            // SECTION: 카드취소
            if (
              payment_method === 'CARD' ||
              payment_method === 'NAVER_PAY' ||
              payment_method === 'KAKAO_PAY'
            ) {
              cancelRes = await cancelCard(cancelData);
            }
            // SECTION: 휴대폰결제
            else if (payment_method === 'MOBILE') {
              cancelRes = await cancelMobile(cancelData);
            }
            // SECTION: 계좌이체
            else if (
              payment_method === 'TRANSFER' ||
              payment_method === 'VIRTUAL_ACCOUNT'
            ) {
              cancelRes = await cancelAccount(cancelData);
            }

            // NOTE: 결제 취소 실패 시 환불 x
            if (cancelRes?.data?.code !== 'A0200') {
              console.log('여기 ..? ', cancelRes);
              MessageAlert.warning('결제 취소에 실패하였습니다!');
              return;
            }
          }

          // NOTE: 주문취소
          const { status: cancelStatus, data: cancelData } =
            await createRefundDelivery({
              parcel_service_id,
              refund_reason,
              refund_amount: refund_amount + isLast,
              refund_num,
              refund_payment_info: cancelRes ? cancelRes?.data?.data : null,
            });

          // NOTE: 주문취소 실패 처리
          if (cancelStatus !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newShopOrderList = orderList.map((order) =>
            order.shop_order_id !== shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          refund: cancelData,
                          delivery_status: 'RETURN_COMPLETED',
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newShopOrderList);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelStatus;
        }
        // SECTION: 환불요청
        case 'DELIVERY-REFUNDED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE -
           *    shop_order_id: number
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *  }
           */
          if (!confirm('환불/반품 요청하시겠습니까?')) return;

          setIsLoading(true);

          // NOTE: 환불
          const { status: refundStatus, data: refundData } =
            selectedTab === 'SUBSCRIPTION'
              ? await createRefundSubscription({
                  parcel_service_id: data.parcel_service_id,
                  refund_reason: data.refund_reason,
                  return_num: data.return_num,
                })
              : await createRefundDelivery({
                  parcel_service_id: data.parcel_service_id,
                  refund_reason: data.refund_reason,
                  return_num: data.return_num,
                });

          if (refundStatus !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newShopList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          refund: refundData,
                          delivery_status: 'RETURN_REQUESTED',
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newShopList);

          MessageAlert.success('환불이 성공적으로 접수되었습니다!');
          return refundStatus;
        }
        // SECTION: 정기배송 구매확정
        case 'SUBSCRIPTION-CONFIRMED': {
          if (
            !confirm(
              '상품 주문을 확정하시겠습니까?\n주문 확정 후 취소/환불은 불가능합니다.',
            )
          )
            return;

          /**
           * STUB data 형식
           * --
           *  {
           *    shop_order_id: number
           *
           *    parcel_service_id: number
           *    purchase_status: string
           *    regular_payment_id: number
           *    regular_payment_info: object
           *  }
           */

          const newConfirmData = data.regular_payment_info.map((info) =>
            info.regular_payment_id !== data.regular_payment_id
              ? info
              : {
                  ...info,
                  purchase_status: data.purchase_status,
                },
          );
          const { status: regularConfirmedStatus } =
            await confirmParcelServiceStatus({
              parcel_service_id: data.parcel_service_id,
              regular_payment_id: data.regular_payment_id,
            });

          if (regularConfirmedStatus !== 200) {
            MessageAlert.warning('주문 확정에 실패하였습니다!');
            return;
          }

          const newRegularOrderList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          regular_payment_info: newConfirmData,
                        },
                  ),
                },
          );
          userDataAction.setData('orderList', newRegularOrderList);

          MessageAlert.success('상품 주문이 확정되었습니다.');
          return;
        }
        // SECTION: 정기배송 주문취소
        case 'SUBSCRIPTION-CANCELLED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    regular_payment_info: object
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *    tax_rate: number
           *    isFirst: boolean
           *  }
           */
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          let regularCancelRes = null;
          let cancelAmount = 0;

          // 1회차 배송일 경우
          if (data.isFirst) {
            // 해당 주문건 찾기
            const shopOrder = orderList.find(
              (order) => order.shop_order_id === data.shop_order_id,
            );

            // 취소 sequence 번호 계산
            const sequence = shopOrder?.parcel_service.reduce((acc, parcel) => {
              const { refund } = parcel;

              if (refund) {
                ++acc;
              }

              return acc;
            }, 1);

            // 마지막 환불건인지 체크
            // 마지막 배송건이면 배송비 리턴
            const isLast =
              shopOrder.parcel_service.length === sequence
                ? (shopOrder.final_delivery_price ?? 0)
                : 0;

            // NOTE: 결제취소
            cancelAmount = data.regular_payment_info?.price + isLast;
            const cancelData = {
              mid: POINT_STORE_ID,
              cancelType: 'PARTIAL',
              orgTradeKeyType: 'TID',
              orgTradeKey: `${data.regular_payment_info.payment_info?.tid}`,
              cancelTotalAmount: `${cancelAmount}`,
              cancelTaxFreeAmount: `${data.tax_free ?? 0}`,
              cancelSeq: `${sequence}`,
            };

            // SECTION: 카드취소
            regularCancelRes = await cancelCard(cancelData);

            // NOTE: 결제 취소 실패 시
            if (
              regularCancelRes.status !== 200 ||
              regularCancelRes.data.code !== 'A0200'
            ) {
              MessageAlert.warning('주문 취소에 실패하였습니다!');
              return;
            }
          }
          // 2회차 이상일 경우 주문 취소
          else {
            // 정기배송이 결제되었을 경우 결제 취소
            if (data.regular_payment_info.payment_info) {
              const regularCancel = {
                mid: POINT_STORE_ID,
                cancelType: 'FULL',
                orgTradeKeyType: 'TID',
                orgTradeKey: data.regular_payment_info.payment_info?.tid,
              };

              cancelAmount = data.regular_payment_info?.price;
              regularCancelRes = await cancelCard(regularCancel);

              // NOTE: 결제 취소 실패 시
              if (
                regularCancelRes.status !== 200 ||
                regularCancelRes.data.code !== 'A0200'
              ) {
                MessageAlert.warning('주문 취소에 실패하였습니다!');
                return;
              }
            }
          }

          const cancelRegular = await createRefundSubscription({
            parcel_service_id: data.parcel_service_id,
            refund_reason: data.refund_reason,
            refund_amount: cancelAmount,
            refund_num: data.regular_payment_info.num,
            refund_payment_info: regularCancelRes.data.data,
            regular_payment_id: data.regular_payment_info.regular_payment_id,
          });

          if (cancelRegular.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newParcelCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          regular_payment_info: parcel.regular_payment_info.map(
                            (regular) =>
                              regular.regular_payment_id !==
                              data.regular_payment_info.regular_payment_id
                                ? regular
                                : {
                                    ...regular,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                    delivery_status: 'RETURN_COMPLETED',
                                  },
                          ),
                          refund: cancelRegular.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newParcelCancelData);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelRegular.status;
        }
        // SECTION: 정기배송 환불
        case 'SUBSCRIPTION-REFUNDED': {
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    parcel_service_id: number
           *    regular_payment_info: object
           *    refund_reason: string
           *  }
           */
          if (!confirm('상품 환불/반품 요청하시겠습니까?')) return;

          setIsLoading(true);

          const cancelRegular = await createRefundSubscription({
            parcel_service_id: data.parcel_service_id,
            refund_reason: data.refund_reason,
            refund_amount: data.regular_payment_info.price,
            refund_num: data.regular_payment_info.num,
            refund_payment_info: null,
            regular_payment_id: data.regular_payment_info.regular_payment_id,
          });

          if (cancelRegular.status !== 200) {
            MessageAlert.warning('환불/반품 신청에 실패하였습니다!');
            return;
          }

          const newParcelCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED_REQUESTED',
                          regular_payment_info: parcel.regular_payment_info.map(
                            (regular) =>
                              regular.regular_payment_id !==
                              data.regular_payment_info.regular_payment_id
                                ? regular
                                : {
                                    ...regular,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                    delivery_status: 'REFUNDED_REQUESTED',
                                  },
                          ),
                          refund: cancelRegular.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newParcelCancelData);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelRegular.status;
        }
        case 'RENTAL-CANCELLED': {
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          let rentalCancelRes = null;
          // 렌탈 결제 취소
          const rentalCancel = {
            mid: POINT_STORE_ID,
            cancelType: 'FULL',
            orgTradeKeyType: 'TID',
            orgTradeKey: data?.rental_payment_info?.at(0)?.payment_info?.tid,
          };

          rentalCancelRes = await cancelCard(rentalCancel);

          // NOTE: 결제 취소 실패 시
          if (
            rentalCancelRes.status !== 200 ||
            rentalCancelRes.data.code !== 'A0200'
          ) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const cancelRental = await createRefundRental({
            rental_id: data.rental_id,
            refund_reason: data.refund_reason,
            rental_payment_id: data.rental_payment_id,
            refund_payment_info: rentalCancelRes.data.data,
          });

          if (cancelRental.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newRentalCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  rental: order.rental.map((rental) =>
                    rental.rental_id !== data.rental_id
                      ? rental
                      : {
                          ...rental,
                          purchase_status: 'REFUNDED',
                          rental_payment_info: rental.rental_payment_info.map(
                            (info) =>
                              info.rental_payment_id !== data.rental_payment_id
                                ? info
                                : {
                                    ...info,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                  },
                          ),
                          refund: cancelRental.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newRentalCancelData);
          MessageAlert.success('상품 주문이 취소되었습니다!');
          return rentalCancel.status;
        }
        case 'RENTAL-REFUNDED': {
          if (
            !confirm(
              '계약 해지 요청을 하시겠습니까?\n계약 해지 시 위약금이 발생할 수 있습니다.',
            )
          )
            return;

          const refundRental = await createRefundRental({
            rental_id: data.rental_id,
            refund_reason: data.refund_reason,
            rental_payment_id: data.rental_payment_id,
          });

          if (refundRental.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newRentalRefundData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  rental: order.rental.map((rental) =>
                    rental.rental_id !== data.rental_id
                      ? rental
                      : {
                          ...rental,
                          purchase_status: 'REFUNDED',
                          rental_payment_info: rental.rental_payment_info.map(
                            (info) =>
                              info.rental_payment_id !== data.rental_payment_id
                                ? info
                                : {
                                    ...info,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                  },
                          ),
                          refund: refundRental.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newRentalRefundData);
          MessageAlert.success('계약 해지 신청되었습니다!');
          return refundRental.status;
        }
        default: {
          return MessageAlert.info('주문 상태 변경 실패!');
        }
      }
    } catch (err) {
      console.error('[handleOrderStatus] Error!! : ', err);
      MessageAlert.error(
        '상품 취소/환불에 실패하였습니다! 관리자에게 문의해주세요!',
      );
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 쇼핑몰 오더 데이터 정제
   * --
   */
  const OrderList = React.useMemo(() => {
    const newOrderList = orderList
      .map((order) => ({
        ...order,
        parcel_service: order?.parcel_service?.map((parcel) => ({
          ...parcel,
          review: reviews.find(
            (review) => parcel.parcel_service_id === review.parcel_service_id,
          ),
        })),
        rental: order?.rental?.map((rental) => ({
          ...rental,
          review: reviews.find(
            (review) => rental.rental_id === review.rental_id,
          ),
        })),
      }))
      .sort((a, b) => b.created_at - a.created_at);

    return newOrderList;
  }, [orderList, reviews]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageOrderPresenter
        order={OrderList}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onOrderStatus={handleOrderStatus}
        navigate={navigate}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        screenSize={screenSize}
        type={deliveryType}
      />
    </>
  );
};

export default MypageOrderContainer;
